import { useRef, useState } from 'react';

// ---------------------------------------------------------------------------
// SignatureStep — review the generated application and sign it in-app.
// Draws on a canvas (mouse + touch), captures a PNG, posts to /sign.
// ---------------------------------------------------------------------------

interface SignatureStepProps {
  token: string;
  onSigned: () => void;
}

export function SignatureStep({ token, onSigned }: SignatureStepProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawing = useRef(false);
  const [hasDrawn, setHasDrawn] = useState(false);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  function pos(e: React.PointerEvent<HTMLCanvasElement>) {
    const c = canvasRef.current;
    if (!c) return { x: 0, y: 0 };
    const r = c.getBoundingClientRect();
    return {
      x: (e.clientX - r.left) * (c.width / r.width),
      y: (e.clientY - r.top) * (c.height / r.height),
    };
  }

  function start(e: React.PointerEvent<HTMLCanvasElement>) {
    e.preventDefault();
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    drawing.current = true;
    const { x, y } = pos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#0f172a';
  }
  function move(e: React.PointerEvent<HTMLCanvasElement>) {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    const { x, y } = pos(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasDrawn(true);
  }
  function end() {
    drawing.current = false;
  }
  function clear() {
    const c = canvasRef.current;
    const ctx = c?.getContext('2d');
    if (c && ctx) ctx.clearRect(0, 0, c.width, c.height);
    setHasDrawn(false);
  }

  async function submit() {
    if (name.trim().length < 2) {
      setError('Please type your full name.');
      return;
    }
    setError(undefined);
    setBusy(true);
    try {
      const signature_image = hasDrawn ? canvasRef.current?.toDataURL('image/png') : undefined;
      const res = await fetch(`/api/intake/${token}/sign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ applicant_name: name, signature_image }),
      });
      if (!res.ok) throw new Error('sign failed');
      onSigned();
    } catch {
      setError("We couldn't save your signature. Please try again.");
      setBusy(false);
    }
  }

  return (
    <div className="rounded-2xl bg-white border border-slate-200 shadow-sm p-6 sm:p-8 md:p-10">
      <h2 className="text-2xl font-semibold text-slate-900 mb-2">
        Review &amp; sign your application
      </h2>
      <p className="text-slate-600 mb-4">
        We've prepared your application from your answers.{' '}
        <a
          href={`/api/intake/${token}/application.pdf`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:text-blue-700 font-medium underline"
        >
          Review it (PDF)
        </a>
        , then sign below to submit.
      </p>

      <label className="block text-sm font-medium text-slate-700 mb-2" htmlFor="applicant-name">
        Full name
      </label>
      <input
        id="applicant-name"
        type="text"
        value={name}
        onChange={(e) => {
          setError(undefined);
          setName(e.target.value);
        }}
        placeholder="Your full legal name"
        className="w-full rounded-lg border border-slate-300 px-4 py-3 text-slate-900 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none mb-4"
      />

      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-slate-700">Signature</span>
        <button
          type="button"
          onClick={clear}
          className="text-sm text-slate-500 hover:text-slate-700"
        >
          Clear
        </button>
      </div>
      <canvas
        ref={canvasRef}
        width={600}
        height={180}
        onPointerDown={start}
        onPointerMove={move}
        onPointerUp={end}
        onPointerLeave={end}
        className="w-full h-44 rounded-lg border border-slate-300 bg-slate-50 touch-none cursor-crosshair"
      />
      <p className="mt-2 text-xs text-slate-400">
        Draw your signature above (optional — your typed name is recorded either way).
      </p>

      {error && <p className="mt-3 text-sm text-red-600">{error}</p>}

      <p className="mt-4 text-xs text-slate-500">
        By submitting, you declare the information provided is true and complete to the best of your
        knowledge. This application does not bind coverage.
      </p>

      <button
        type="button"
        onClick={submit}
        disabled={busy}
        className="mt-4 w-full rounded-lg bg-blue-600 hover:bg-blue-700 disabled:bg-slate-400 text-white px-8 py-3 font-medium transition shadow-sm"
      >
        {busy ? 'Submitting…' : 'Sign & submit application'}
      </button>
    </div>
  );
}
